import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder.js';
import clsx from 'clsx';

/**
 * Voice-driven follow-up capture.
 *
 * BDM taps the mic, speaks a single sentence ("remind me to call PLM next
 * Tuesday about the brake quote"), the server transcribes + parses with
 * Claude, and we either save directly (one-tap confirm) or hand off to the
 * regular FollowupEditor for tweaking.
 *
 * Props:
 *   onClose — close the modal
 *   onSaved(row) — called after successful save
 *   onEdit({ initial }) — caller opens FollowupEditor with the parsed initial values
 *   defaultCustomerId — pre-fill if invoked from a customer page
 */
export default function VoiceFollowupCapture({ onClose, onSaved, onEdit, defaultCustomerId }) {
  const rec = useVoiceRecorder();
  const [parseResult, setParseResult] = useState(null);
  const [serverError, setServerError] = useState(null);
  const qc = useQueryClient();

  const parse = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('audio', rec.blob, `followup-${Date.now()}.webm`);
      const r = await api.post('/followups/parse-voice', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data.data;
    },
    onSuccess: (data) => {
      setServerError(null);
      setParseResult(data);
    },
    onError: (err) => {
      setServerError(err?.response?.data?.error || 'Could not parse the voice command. Try again.');
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const p = parseResult.parsed;
      const body = {
        title: p.title,
        description: p.description || null,
        due_date: p.due_date, // YYYY-MM-DD; backend accepts as TIMESTAMPTZ
        urgency: p.urgency,
        touch_type: p.touch_type,
        customer_id: parseResult.customer_match?.id || defaultCustomerId || null,
      };
      const r = await api.post('/followups', body);
      return r.data.data;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['followups'] });
      onSaved?.(row);
    },
  });

  const submitting = parse.isPending || save.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-baseline justify-between gap-2">
          <h3 className="text-lg font-semibold">🎙 Voice follow-up</h3>
          <button onClick={onClose} className="text-muted hover:text-crimson text-sm">✕</button>
        </header>

        {!parseResult && (
          <>
            <p className="text-sm text-muted">
              Try: <em>"Remind me to call PLM next Tuesday about the brake quote"</em>
            </p>

            <div className="card p-6 text-center">
              {rec.error && <div className="text-crimson text-sm mb-2">{rec.error}</div>}
              {!rec.blob && (
                <>
                  <button
                    onClick={rec.recording ? rec.stop : rec.start}
                    disabled={submitting}
                    className={clsx(
                      'rounded-full w-24 h-24 mx-auto text-4xl shadow-panel transition',
                      rec.recording ? 'bg-crimson text-white animate-pulse' : 'bg-navy text-white hover:bg-navy-600'
                    )}>
                    {rec.recording ? '■' : '●'}
                  </button>
                  <div className="mt-3 text-sm text-muted">
                    {rec.recording ? `Listening… ${formatDuration(rec.durationSec)}` : 'Tap and speak'}
                  </div>
                </>
              )}
              {rec.blob && (
                <>
                  <audio controls src={URL.createObjectURL(rec.blob)} className="w-full mt-2" />
                  <div className="text-sm text-muted mt-2">{formatDuration(rec.durationSec)}</div>
                  <div className="flex justify-center gap-2 mt-3">
                    <button className="btn-ghost" onClick={rec.reset} disabled={submitting}>Re-record</button>
                    <button className="btn-primary" onClick={() => parse.mutate()} disabled={submitting}>
                      {parse.isPending ? 'Parsing…' : 'Parse it'}
                    </button>
                  </div>
                </>
              )}
              {serverError && <div className="text-crimson text-sm mt-2">{serverError}</div>}
            </div>
          </>
        )}

        {parseResult && (
          <ParsedPreview
            data={parseResult}
            saving={save.isPending}
            saveError={save.error?.response?.data?.error}
            onSave={() => save.mutate()}
            onEdit={() => {
              const p = parseResult.parsed;
              onEdit?.({
                title: p.title,
                description: p.description || '',
                due_date: p.due_date || '',
                urgency: p.urgency,
                touch_type: p.touch_type,
                customer_id: parseResult.customer_match?.id || defaultCustomerId || '',
              });
            }}
            onRetry={() => {
              setParseResult(null);
              rec.reset();
            }}
          />
        )}
      </div>
    </div>
  );
}

function ParsedPreview({ data, saving, saveError, onSave, onEdit, onRetry }) {
  const p = data.parsed;
  const isFollowup = p.intent === 'followup';
  const confidenceColor = p.confidence === 'high' ? 'text-emerald-700'
                       : p.confidence === 'medium' ? 'text-gold' : 'text-crimson';

  return (
    <div className="space-y-3">
      <div className="bg-panel/50 rounded p-2 text-xs text-muted">
        <span className="font-semibold">You said:</span> "{data.transcript}"
      </div>

      {!isFollowup && (
        <div className="card p-3 border-l-4 border-gold">
          <div className="text-sm">
            That didn't sound like a follow-up command. Re-record, or open the editor to start from scratch.
          </div>
        </div>
      )}

      {isFollowup && (
        <div className="card p-4 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="section-eyebrow">Parsed follow-up</div>
            <div className={clsx('text-xs font-medium', confidenceColor)}>
              {p.confidence} confidence
            </div>
          </div>
          <div className="rule-crimson" />

          <PreviewRow label="What" value={p.title} />
          <PreviewRow
            label="When"
            value={p.due_date
              ? `${formatDate(p.due_date)}${p.due_date_natural ? ` (${p.due_date_natural})` : ''}`
              : <span className="text-crimson italic">no date specified</span>}
          />
          <PreviewRow
            label="Customer"
            value={
              data.customer_match
                ? <span><span className="text-emerald-700">✓</span> {data.customer_match.name}</span>
                : p.customer_name_guess
                  ? <span className="text-gold">{p.customer_name_guess} <span className="text-muted text-xs">(no exact match — confirm in editor)</span></span>
                  : <span className="text-muted italic">none mentioned</span>
            }
          />
          <PreviewRow label="Type" value={<span className="capitalize">{p.touch_type}</span>} />
          <PreviewRow label="Urgency" value={
            <span className={clsx('capitalize',
              p.urgency === 'high' && 'text-crimson font-medium',
              p.urgency === 'medium' && 'text-gold')}>
              {p.urgency}
            </span>
          } />
          {p.description && p.description !== p.title && (
            <PreviewRow label="Note" value={<span className="text-muted">{p.description}</span>} />
          )}
        </div>
      )}

      {saveError && <div className="text-crimson text-sm">{saveError}</div>}

      <div className="flex justify-between gap-2 pt-2 flex-wrap">
        <button className="btn-ghost text-sm" onClick={onRetry} disabled={saving}>
          ← Re-record
        </button>
        <div className="flex gap-2 ml-auto">
          <button className="btn-ghost" onClick={onEdit} disabled={saving}>
            Open in editor
          </button>
          {isFollowup && p.due_date && (
            <button className="btn-primary" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : '✓ Confirm & save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-[10px] uppercase tracking-wider text-muted w-16 shrink-0">{label}</span>
      <span className="flex-1">{value}</span>
    </div>
  );
}

function formatDuration(s) {
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
